import { mergeRemoteLink } from "../tasks/remote-link.js";
import type { Task } from "../tasks/types.js";
import { compareVersions, recordsEqual, unionField } from "./compare.js";
import {
  BOOKKEEPING_FIELDS,
  LINK_FIELDS,
  type EntityKind,
  type FieldConflict,
  type RecordMerge,
  type SyncableRecord,
} from "./types.js";

/**
 * Merge two versions of one record (same id) from local and remote.
 *
 * - A side that is missing means the other side is authoritative (and the missing
 *   side must adopt it).
 * - When both exist, scalars are resolved last-write-wins via {@link compareVersions}
 *   (rev → updated_at → stable tiebreak); set fields (`setFields`) are UNION-merged
 *   so concurrent additions on different devices both survive.
 * - LINK fields (`LINK_FIELDS`, the Google remote link) are resolved by their own
 *   `link_rev` rather than the record's LWW order, because they are adopted
 *   without bumping `updated_at`. Higher rev wins the whole block; equal rev is
 *   grow-only per field. This is what makes add / replace / UNLINK all converge.
 * - Soft-delete rides on `deleted_at` as an ordinary scalar: the later of an
 *   edit-vs-delete wins (LWW resurrect/tombstone). Tombstones are kept, not dropped,
 *   so deletes propagate.
 * - Scalar values discarded from the losing side are returned as `conflicts` so the
 *   caller can preserve them rather than silently lose a concurrent edit.
 *
 * Pure and symmetric: `mergeRecord(a,b)` and `mergeRecord(b,a)` yield the same
 * `merged`, which is what makes multi-device convergence work.
 */
export function mergeRecord<T extends SyncableRecord>(
  kind: EntityKind,
  local: T | undefined,
  remote: T | undefined,
  setFields: readonly string[],
): RecordMerge<T> {
  if (local && !remote) {
    return { merged: local, conflicts: [], changedLocally: false, changedRemotely: true };
  }
  if (!local && remote) {
    return { merged: remote, conflicts: [], changedLocally: true, changedRemotely: false };
  }
  if (!local || !remote) {
    throw new Error("mergeRecord requires at least one side");
  }

  const cmp = compareVersions(local, remote);
  const winner = cmp >= 0 ? local : remote;
  const loser = cmp >= 0 ? remote : local;

  const merged = { ...winner } as T;
  for (const f of setFields) {
    (merged as Record<string, unknown>)[f] = unionField(
      (local as Record<string, unknown>)[f],
      (remote as Record<string, unknown>)[f],
    );
  }
  // The remote link is ordered by its OWN revision, not by the record's LWW
  // order — it is adopted without bumping `updated_at`, so the row whose content
  // wins is not necessarily the row carrying the newer link. Graft the winning
  // block on separately, which is also what lets an explicit UNLINK (a newer rev
  // whose ids are null) propagate instead of being treated as missing data.
  const linkFields = LINK_FIELDS[kind];
  if (linkFields.length > 0) {
    Object.assign(merged, mergeRemoteLink(local as unknown as Task, remote as unknown as Task));
  }

  return {
    merged,
    conflicts: scalarConflicts(kind, winner, loser, [...setFields, ...linkFields]),
    changedLocally: !recordsEqual(merged, local),
    changedRemotely: !recordsEqual(merged, remote),
  };
}

function scalarConflicts<T extends SyncableRecord>(
  kind: EntityKind,
  winner: T,
  loser: T,
  setFields: readonly string[],
): FieldConflict[] {
  const skip = new Set<string>([...BOOKKEEPING_FIELDS, ...setFields]);
  const out: FieldConflict[] = [];
  for (const field of Object.keys(winner)) {
    if (skip.has(field)) continue;
    const kept = (winner as Record<string, unknown>)[field];
    const discarded = (loser as Record<string, unknown>)[field];
    if (!recordsEqual(kept, discarded)) {
      out.push({ kind, id: winner.id, field, kept, discarded });
    }
  }
  return out;
}
